/**
 * Runs tab — run history + "Run Backtest" + "Dry Run" (Phases 5–6) + the
 * SuperAdmin utilities panel. Live runs launch from a completed dry run's
 * detail view (RunDetail → LiveRunModal, Phase 9 / PLAN §8.2).
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Code,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { payrollApi } from '../api';
import BacktestView from './BacktestView';
import RunDetail from './RunDetail';

const STATUS_COLORS = { completed: 'green', running: 'blue', failed: 'red', cancelled: 'gray' };
const TYPE_COLORS = { backtest: 'purple', dry: 'teal', live: 'orange' };

const fmtWhen = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

/** Post-rehearsal modal — the copy links are the deliverable. */
function RehearsalResultModal({ result, onClose, onViewRun }) {
  const totals = (result && result.totals) || {};
  const outside = totals.outside1099 || {};
  const w2 = totals.w2 || {};
  return (
    <Modal isOpen={!!result} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Rehearsal complete — copies ready to compare</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Alert status="success" borderRadius="md" mb={4} fontSize="sm">
            <AlertIcon />
            Payroll ran against fresh copies only — the real Process Pipeline and Bank &amp; LO
            Workbook were not touched.
          </Alert>
          <Stack spacing={2} mb={4}>
            <Button
              as="a"
              href={result ? result.workbookCopyUrl : '#'}
              target="_blank"
              rel="noopener noreferrer"
              colorScheme="green"
              rightIcon={<ExternalLinkIcon />}
            >
              Open Workbook Copy (2026 book)
            </Button>
            <Button
              as="a"
              href={result ? result.pipelineCopyUrl : '#'}
              target="_blank"
              rel="noopener noreferrer"
              variant="outline"
              rightIcon={<ExternalLinkIcon />}
            >
              Open Pipeline Copy
            </Button>
          </Stack>
          <Box borderWidth="1px" borderRadius="md" p={3} fontSize="sm">
            <Text fontWeight="semibold" mb={1}>Where to look in the copies</Text>
            <Text>
              Batch <Code fontSize="xs">{result ? result.batchLabel : ''}</Code>
              {totals.pipelineWritten != null && <> — {totals.pipelineWritten} pipeline rows written</>}
            </Text>
            {outside.count ? (
              <Text mt={1}>
                {outside.count} × 1099 rows pasted in <b>Outside LO Comp</b> starting at row {outside.pasteRow}
              </Text>
            ) : (
              <Text mt={1} color="gray.500">No 1099 rows this run.</Text>
            )}
            {w2.count ? (
              <Text mt={1}>
                {w2.count} × W2 rows pasted in <b>&quot;{w2.tab}&quot;</b> starting at row {w2.pasteRow}
              </Text>
            ) : (
              <Text mt={1} color="gray.500">No W2 rows this run.</Text>
            )}
            {totals.trueupCount ? <Text mt={1}>{totals.trueupCount} true-up append(s) on the pipeline copy.</Text> : null}
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={2} onClick={onClose}>Close</Button>
          <Button colorScheme="blue" onClick={onViewRun}>View run detail</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function RunsTab({
  runs, loading, loadError, onRefresh, onOpenInCalculator, isSuperAdmin,
  openRunId = null, onOpenRunConsumed = null,
}) {
  const toast = useToast();
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const [backtestBusy, setBacktestBusy] = useState(false);
  const [dryRunBusy, setDryRunBusy] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [utilBusy, setUtilBusy] = useState(null); // 'setup' | 'backfill' | null
  const [rehearsalBusy, setRehearsalBusy] = useState(false);
  const [rehearsalResult, setRehearsalResult] = useState(null);
  const rehearsalConfirm = useDisclosure();

  const runBacktest = async () => {
    setBacktestBusy(true);
    try {
      const res = await payrollApi.backtest();
      toast({
        title: 'Backtest complete',
        description: `${res.tally.MATCH} match / ${res.tally.MISMATCH} mismatch (rate ${res.matchRate}%), Σ|Δ| $${res.sumAbsDelta}`,
        status: 'success',
        duration: 8000,
        isClosable: true,
      });
      await onRefresh();
      openRun(res.runId);
    } catch (err) {
      toast({ title: 'Backtest failed', description: err.message, status: 'error', duration: null, isClosable: true });
    } finally {
      setBacktestBusy(false);
    }
  };

  const runDry = async () => {
    setDryRunBusy(true);
    try {
      const res = await payrollApi.dryRun();
      toast({
        title: 'Dry run complete — preview only, nothing written',
        description: `${res.processed} unprocessed rows → ${res.payeeCount} payee rows (${res.totals.trueupCount} true-ups, ${res.totals.needsReview} need review)`,
        status: 'success',
        duration: 8000,
        isClosable: true,
      });
      await onRefresh();
      openRun(res.runId);
    } catch (err) {
      toast({ title: 'Dry run failed', description: err.message, status: 'error', duration: null, isClosable: true });
    } finally {
      setDryRunBusy(false);
    }
  };

  const runRehearsal = async () => {
    rehearsalConfirm.onClose();
    setRehearsalBusy(true);
    try {
      const res = await payrollApi.rehearsalRun();
      setRehearsalResult(res);
      await onRefresh();
    } catch (err) {
      const runId = err.details && err.details.runId;
      toast({
        title: 'Rehearsal failed',
        description: `${err.message}${runId ? ` (run ${runId} — open it in the list to resume)` : ''}`,
        status: 'error', duration: null, isClosable: true,
      });
      await onRefresh();
    } finally {
      setRehearsalBusy(false);
    }
  };

  const runUtility = async (kind) => {
    setUtilBusy(kind);
    try {
      if (kind === 'setup') {
        const res = await payrollApi.setupPipelineColumns();
        toast({
          title: 'Pipeline columns ready',
          description: res.added.length
            ? `Appended: ${res.added.join(', ')}`
            : 'All payroll columns already present — nothing changed.',
          status: 'success', duration: 8000, isClosable: true,
        });
      } else {
        const res = await payrollApi.backfillLegacyRows();
        toast({
          title: 'Legacy backfill complete',
          description: `${res.stamped} rows stamped LEGACY, ${res.keptExisting} already stamped, ${res.remaining} left unprocessed.${res.warning ? ` ⚠ ${res.warning}` : ''}`,
          status: res.warning ? 'warning' : 'success', duration: null, isClosable: true,
        });
      }
    } catch (err) {
      toast({ title: kind === 'setup' ? 'Setup failed' : 'Backfill failed', description: err.message, status: 'error', duration: null, isClosable: true });
    } finally {
      setUtilBusy(null);
    }
  };

  const openRun = async (runId) => {
    setSelectedRunId(runId);
    setDetailLoading(true);
    setDetail(null);
    try {
      setDetail(await payrollApi.getRunDetail({ runId }));
    } catch (err) {
      toast({ title: 'Could not load run', description: err.message, status: 'error', duration: 6000, isClosable: true });
      setSelectedRunId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  // Schedule tab → "view run": open the requested run once, then hand back.
  useEffect(() => {
    if (openRunId) {
      openRun(openRunId);
      if (onOpenRunConsumed) onOpenRunConsumed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRunId]);

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={3} gap={2} wrap="wrap">
        <Text fontSize="sm" color="gray.500">
          Launch runs here, or set up automatic ones in the Schedule tab — those show a{' '}
          <Badge variant="outline" colorScheme="cyan">scheduled</Badge> badge.
        </Text>
        <Flex gap={2}>
          <Button size="sm" variant="outline" onClick={onRefresh} isDisabled={loading}>Refresh</Button>
          <Button size="sm" colorScheme="purple" onClick={runBacktest} isLoading={backtestBusy}
            loadingText="Backtesting…">
            Run Backtest
          </Button>
          <Button size="sm" colorScheme="teal" onClick={runDry} isLoading={dryRunBusy}
            loadingText="Computing…">
            Dry Run (preview)
          </Button>
          {isSuperAdmin && (
            <Button size="sm" colorScheme="orange" variant="outline" onClick={rehearsalConfirm.onOpen}
              isLoading={rehearsalBusy} loadingText="Copying & running…">
              Run Payroll on Fresh Copies
            </Button>
          )}
        </Flex>
      </Flex>

      {loadError && (
        <Alert status="error" mb={3} borderRadius="md"><AlertIcon />{loadError}</Alert>
      )}

      {isSuperAdmin && (
        <Flex align="center" gap={2} mb={3} p={2} borderWidth="1px" borderRadius="md" borderStyle="dashed" wrap="wrap">
          <Text fontSize="xs" color="gray.500" flex="1" minW="240px">
            One-time pipeline utilities (SuperAdmin, audited): add the payroll columns to the live
            tab, then stamp LEGACY on everything paid under the old process. Writes to the sheet.
          </Text>
          <Button size="xs" variant="outline" onClick={() => runUtility('setup')}
            isLoading={utilBusy === 'setup'} isDisabled={!!utilBusy}>
            Setup Pipeline Columns
          </Button>
          <Button size="xs" variant="outline" colorScheme="orange" onClick={() => runUtility('backfill')}
            isLoading={utilBusy === 'backfill'} isDisabled={!!utilBusy}>
            Backfill LEGACY Rows
          </Button>
        </Flex>
      )}

      <Box overflowX="auto" borderWidth="1px" borderRadius="md">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Started (ET)</Th>
              <Th>Type</Th>
              <Th>Status</Th>
              <Th isNumeric>Rows</Th>
              <Th isNumeric>Match rate</Th>
              <Th>Engine</Th>
              <Th>Initiator</Th>
            </Tr>
          </Thead>
          <Tbody>
            {runs.length === 0 && (
              <Tr><Td colSpan={7}><Text py={4} textAlign="center" color="gray.500">No runs yet — start with a backtest.</Text></Td></Tr>
            )}
            {runs.map((run) => (
              <Tr
                key={run.id}
                cursor="pointer"
                bg={run.id === selectedRunId ? hoverBg : undefined}
                _hover={{ bg: hoverBg }}
                onClick={() => openRun(run.id)}
              >
                <Td whiteSpace="nowrap">{fmtWhen(run.started_at)}</Td>
                <Td>
                  <Badge colorScheme={TYPE_COLORS[run.run_type] || 'gray'}>{run.run_type}</Badge>
                  {run.schedule_id && <Badge ml={1} variant="outline" colorScheme="cyan">scheduled</Badge>}
                </Td>
                <Td>
                  <Badge colorScheme={STATUS_COLORS[run.status] || 'gray'}>{run.status}</Badge>
                  {run.error && (
                    <Text fontSize="xs" color="red.400" maxW="260px" isTruncated title={run.error}>{run.error}</Text>
                  )}
                </Td>
                <Td isNumeric>{run.rows_scanned == null ? '—' : run.rows_scanned}</Td>
                <Td isNumeric>
                  {run.totals && run.totals.matchRate != null ? `${run.totals.matchRate}%` : '—'}
                </Td>
                <Td>
                  <Text fontSize="xs" maxW="220px" isTruncated title={run.engine_version}>{run.engine_version}</Text>
                </Td>
                <Td><Text fontSize="xs">{run.initiated_by_email || run.initiated_by_uid || '—'}</Text></Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {detailLoading && <Flex justify="center" py={10}><Spinner /></Flex>}
      {detail && detail.run && detail.run.run_type === 'backtest' && (
        <BacktestView detail={detail} onOpenInCalculator={onOpenInCalculator} />
      )}
      {detail && detail.run && detail.run.run_type !== 'backtest' && (
        <RunDetail
          detail={detail}
          isSuperAdmin={isSuperAdmin}
          onRunLaunched={async (runId) => { await onRefresh(); openRun(runId); }}
        />
      )}

      <Modal isOpen={rehearsalConfirm.isOpen} onClose={rehearsalConfirm.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Run payroll on fresh copies?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" mb={3}>
              This creates fresh Google Sheets copies of the <b>Process Pipeline</b> and the
              <b> 2026 Bank &amp; LO Workbook</b> (exactly as they are right now), then runs the full
              payroll against the copies — commissions stamped on the pipeline copy, payee rows
              pasted into the workbook copy.
            </Text>
            <Alert status="info" borderRadius="md" fontSize="sm">
              <AlertIcon />
              The real pipeline and Marilyn&apos;s real 2026 book are <b>not touched</b>. Open the
              workbook copy next to her real book to compare what was added. Takes about 1–3 minutes.
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={rehearsalConfirm.onClose}>Cancel</Button>
            <Button colorScheme="orange" onClick={runRehearsal}>Copy &amp; run</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <RehearsalResultModal
        result={rehearsalResult}
        onClose={() => setRehearsalResult(null)}
        onViewRun={() => {
          const runId = rehearsalResult && rehearsalResult.runId;
          setRehearsalResult(null);
          if (runId) openRun(runId);
        }}
      />
    </Box>
  );
}
